import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
//import { InputMask } from "primereact/inputmask";
import { Dropdown } from "primereact/dropdown";
import { fetchWithAuth, API_URL } from "../Componentes/fetchWithAuth";

const EditarClientes = ({ isOpen, onClose, cliente, language, onSave }) => {
  const [formData, setFormData] = useState(cliente || {});
  const [paises, setPaises] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [ciudades, setCiudades] = useState([]);

  const translations = {
    es: {
      editClient: "Editar Cliente",
      name: "Nombre",
      phone1: "Teléfono 1",
      phone2: "Teléfono 2",
      brand: "Marca",
      country: "País",
      department: "Departamento",
      city: "Ciudad",
      save: "Guardar",
      cancel: "Cancelar",
      errorLoadingData: "Error al cargar datos:",
      errorSavingClient: "Error al guardar cliente:",
      selectCountry: "Seleccione un país",
      selectDepartment: "Seleccione un departamento",
      selectCity: "Seleccione una ciudad",
    },
    en: {
      editClient: "Edit Client",
      name: "Name",
      phone1: "Phone 1",
      phone2: "Phone 2",
      brand: "Brand",
      country: "Country",
      department: "Department",
      city: "City",
      save: "Save",
      cancel: "Cancel",
      errorLoadingData: "Error loading data:",
      errorSavingClient: "Error saving client:",
      selectCountry: "Select a country",
      selectDepartment: "Select a department",
      selectCity: "Select a city",
    },
    zh: {
      editClient: "编辑客户",
      name: "名称",
      phone1: "电话 1",
      phone2: "电话 2",
      brand: "品牌",
      country: "国家",
      department: "省",
      city: "城市",
      save: "保存",
      cancel: "取消",
      errorLoadingData: "加载数据错误:",
      errorSavingClient: "保存客户错误:",
      selectCountry: "选择国家",
      selectDepartment: "选择省",
      selectCity: "选择城市",
    },
  };

  const t = translations[language] || translations.es;

  // [EXPERT_COMMENT]: QUÉ: Carga inicial de datos del cliente y países. POR QUÉ: Este efecto se ejecuta solo cuando el componente se monta o el 'cliente' cambia. Establece los datos del formulario y carga la lista inicial de países.
  // [EXPERT_COMMENT]: QUÉ: Refactorización a un único useEffect para la carga de datos. POR QUÉ: Se centraliza toda la lógica de carga en cascada (países, departamentos, ciudades) en un solo efecto que depende únicamente de 'cliente'. Esto elimina las condiciones de carrera (race conditions) entre múltiples useEffects que dependían de actualizaciones de estado intermedias.
  // [EXPERT_COMMENT]: QUÉ: Refactorización a un único useEffect para la carga de datos. POR QUÉ: Se centraliza toda la lógica de carga en cascada (países, departamentos, ciudades) en un solo efecto que depende únicamente de 'cliente'. Esto elimina las condiciones de carrera (race conditions) entre múltiples useEffects que dependían de actualizaciones de estado intermedias.
  const loadPaises = React.useCallback(async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/paises`);
      const data = await response.json();
      return data.map((pais) => ({
        label: pais.sNombrePais,
        value: pais.idpais,
      }));
    } catch (error) {
      console.error(t.errorLoadingData, error);
      return [];
    }
  }, [t.errorLoadingData]);

  const loadDepartamentos = React.useCallback(
    async (idPais) => {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/api/departamentos/${idPais}`,
        );
        const data = await response.json();
        return data.map((dpto) => ({
          label: dpto.sNombreDepartamento,
          value: dpto.iddepartamento,
        }));
      } catch (error) {
        console.error(t.errorLoadingData, error);
        return [];
      }
    },
    [t.errorLoadingData],
  );

  const loadCiudades = React.useCallback(
    async (idDepartamento) => {
      try {
        console.log(
          "[DEBUG] 2. Cargando ciudades para el departamento ID:",
          idDepartamento,
        );
        const response = await fetchWithAuth(
          `${API_URL}/api/ciudades?departamentoId=${idDepartamento}`,
        );
        const data = await response.json();
        console.log("[DEBUG] 3. Respuesta cruda de la API de ciudades:", data);
        return data.map((ciudad) => ({
          label: ciudad.sNombreCiudad,
          value: ciudad.iCodCiudad,
        }));
      } catch (error) {
        console.error(t.errorLoadingData, error);
        return [];
      }
    },
    [t.errorLoadingData],
  );

  useEffect(() => {
    const loadData = async () => {
      if (!cliente) {
        setFormData({});
        setPaises([]);
        setDepartamentos([]);
        setCiudades([]);
        return;
      }

      console.log("[DEBUG] 1. Cliente recibido:", cliente);

      const paisesData = await loadPaises();
      const departamentosData = cliente.idpais
        ? await loadDepartamentos(cliente.idpais)
        : [];
      const ciudadesData = cliente.iddepartamento
        ? await loadCiudades(cliente.iddepartamento)
        : [];

      console.log(
        "[DEBUG] 4. Datos de ciudades mapeados para el Dropdown:",
        ciudadesData,
      );

      setPaises(paisesData);
      setDepartamentos(departamentosData);
      setCiudades(ciudadesData);
      setFormData(cliente);

      console.log(
        "[DEBUG] 5. Estado final del formulario (formData):",
        cliente,
      );
    };

    loadData();
  }, [cliente, loadCiudades, loadDepartamentos, loadPaises]);

  const handleChange = async (e) => {
    // [EXPERT_ANALYSIS]: QUÉ: Adaptación del manejador de cambios para InputText y Dropdown. POR QUÉ: Los componentes InputText y Dropdown de PrimeReact manejan los eventos de forma diferente. InputText usa `e.target.name` y `e.target.value`, mientras que Dropdown pasa el valor directamente en `e.value` y el `name` debe ser inferido del `id` del componente. Esta lógica unificada permite manejar ambos tipos de componentes. IMPACTO: Corrige la actualización de `formData` para los Dropdown y asegura la carga en cascada de departamentos y ciudades.
    let name;
    let value;

    if (e.target) {
      // [EXPERT_ANALYSIS]: QUÉ: Manejo de eventos para InputText. POR QUÉ: Si el evento tiene `e.target`, se asume que es un InputText y se extraen `name` y `value` de `e.target`. IMPACTO: Mantiene la funcionalidad existente para campos de texto.
      name = e.target.name;
      value = e.target.value;
    } else {
      // [EXPERT_ANALYSIS]: QUÉ: Manejo de eventos para Dropdown. POR QUÉ: Si no hay `e.target`, se asume que es un Dropdown. El valor se obtiene de `e.value` y el nombre del campo se obtiene del `id` del componente que disparó el evento. IMPACTO: Permite que los Dropdown actualicen correctamente el estado del formulario.
      name = e.currentEvent.target.id; // [EXPERT_ANALYSIS]: QUÉ: Obtiene el ID del elemento que disparó el evento. POR QUÉ: PrimeReact Dropdown no pasa `name` en `e.target`, pero el `id` del Dropdown se usa como el nombre lógico del campo en `formData`. IMPACTO: Asocia el valor correcto al campo correspondiente en `formData`.
      value = e.value;
    }

    setFormData((prev) => ({ ...prev, [name]: value })); // [EXPERT_ANALYSIS]: QUÉ: Actualiza el estado del formulario. POR QUÉ: Utiliza el `name` y `value` determinados para actualizar la propiedad correspondiente en `formData`. IMPACTO: Refleja la selección del usuario en el estado del componente.

    if (name === "idpais") {
      // [EXPERT_ANALYSIS]: QUÉ: Lógica para cambio de país. POR QUÉ: Cuando el país cambia, se resetean los departamentos y ciudades, y se cargan los nuevos departamentos. IMPACTO: Asegura la coherencia de los datos dependientes.
      setFormData((prev) => ({
        ...prev,
        iddepartamento: null,
        idciudad: null,
      })); // [EXPERT_ANALYSIS]: QUÉ: Resetea departamento y ciudad. POR QUÉ: Al cambiar el país, las selecciones anteriores de departamento y ciudad dejan de ser válidas. IMPACTO: Evita inconsistencias de datos.
      setCiudades([]); // [EXPERT_ANALYSIS]: QUÉ: Limpia la lista de ciudades. POR QUÉ: Las ciudades dependen del departamento, que se ha reseteado. IMPACTO: Prepara la UI para nuevas selecciones.
      if (value) {
        // [EXPERT_ANALYSIS]: QUÉ: Carga departamentos si hay un país seleccionado. POR QUÉ: Si se ha seleccionado un país válido, se procede a cargar los departamentos asociados. IMPACTO: Permite la selección de departamentos.
        const loadedDepartamentos = await loadDepartamentos(value); // [EXPERT_ANALYSIS]: QUÉ: Llama a la función asíncrona para cargar departamentos. POR QUÉ: Obtiene los departamentos del backend basándose en el ID del país. IMPACTO: Rellena el Dropdown de departamentos.
        setDepartamentos(loadedDepartamentos); // [EXPERT_ANALYSIS]: QUÉ: Actualiza el estado de departamentos. POR QUÉ: Los departamentos cargados se asignan al estado para que el Dropdown los muestre. IMPACTO: Actualiza la UI.
      } else {
        setDepartamentos([]); // [EXPERT_ANALYSIS]: QUÉ: Limpia la lista de departamentos. POR QUÉ: Si no hay país seleccionado, no debe haber departamentos disponibles. IMPACTO: Mantiene la UI limpia.
      }
    } else if (name === "iddepartamento") {
      // [EXPERT_ANALYSIS]: QUÉ: Lógica para cambio de departamento. POR QUÉ: Cuando el departamento cambia, se resetean las ciudades y se cargan las nuevas ciudades. IMPACTO: Asegura la coherencia de los datos dependientes.
      setFormData((prev) => ({ ...prev, idciudad: null })); // [EXPERT_ANALYSIS]: QUÉ: Resetea la ciudad. POR QUÉ: Al cambiar el departamento, la selección anterior de ciudad deja de ser válida. IMPACTO: Evita inconsistencias de datos.
      if (value) {
        // [EXPERT_ANALYSIS]: QUÉ: Carga ciudades si hay un departamento seleccionado. POR QUÉ: Si se ha seleccionado un departamento válido, se procede a cargar las ciudades asociadas. IMPACTO: Permite la selección de ciudades.
        const loadedCiudades = await loadCiudades(value); // [EXPERT_ANALYSIS]: QUÉ: Llama a la función asíncrona para cargar ciudades. POR QUÉ: Obtiene las ciudades del backend basándose en el ID del departamento. IMPACTO: Rellena el Dropdown de ciudades.
        setCiudades(loadedCiudades); // [EXPERT_ANALYSIS]: QUÉ: Actualiza el estado de ciudades. POR QUÉ: Las ciudades cargadas se asignan al estado para que el Dropdown las muestre. IMPACTO: Actualiza la UI.
      } else {
        setCiudades([]); // [EXPERT_ANALYSIS]: QUÉ: Limpia la lista de ciudades. POR QUÉ: Si no hay departamento seleccionado, no debe haber ciudades disponibles. IMPACTO: Mantiene la UI limpia.
      }
    }
  };

  const handleSubmit = () => {
    // [EXPERT_ANALYSIS]: QUÉ: Creación de un objeto 'payload' para el backend. - POR QUÉ: El componente padre que realiza la llamada a la API necesita un objeto con los nombres de campo que el backend espera (ej. 'nombre'). Este componente, 'EditarClientes', tiene el contexto necesario (los arrays de paises y departamentos) para construir este payload correctamente, traduciendo los IDs seleccionados (ej. formData.idpais) a sus nombres en formato string (ej. 'Colombia'), que es lo que la validación del backend requiere para esos campos. - IMPACTO: Desacopla la lógica de formato de datos del componente padre y centraliza la responsabilidad en el componente que gestiona el formulario, asegurando que se envíen los datos correctos a la API.

    const payload = {
      // Se mantiene iCodigo para que el componente padre pueda construir la URL del API.
      iCodigo: formData.iCodigo,
      // Se mapean los campos del estado del formulario a los nombres que espera el backend.
      nombre: formData.sNombreCliente,
      telefono: formData.sTelefono,
      telefono2: formData.sTelefono2,
      correo: formData.sEmail,
      marca: formData.sMarca,
      // [EXPERT_ANALYSIS]: QUÉ: Se eliminó el campo 'direccion' del payload. - POR QUÉ: Para mantener la sincronización con el backend, donde se eliminó el manejo de este campo para evitar errores con la base de datos, ya que la columna 'sDireccion' no existe.
      // [EXPERT_ANALYSIS]: QUÉ: Se eliminó el campo 'nit' del payload. - POR QUÉ: Para mantener la sincronización con el backend, donde se eliminó el manejo de este campo para evitar errores con la base de datos, ya que la columna 'sNit' no existe.
      // [EXPERT_ANALYSIS]: QUÉ: Se usa 'cliente.idciudad' como fallback. - POR QUÉ: El objeto 'cliente' original (pasado como prop) contiene el ID de la ciudad correcto y estable desde el backend. El estado 'formData.idciudad' puede volverse nulo o indefinido si el usuario cambia el país, debido a la lógica de reseteo en cascada de los dropdowns. Usar 'cliente.idciudad' como un valor de respaldo (`||`) garantiza que, incluso si el usuario no vuelve a seleccionar una ciudad, el ID original y correcto siempre se envíe al backend. - IMPACTO: Resuelve de forma robusta el error de validación final para el campo 'ciudad', asegurando que nunca se envíe un valor nulo o indefinido si ya existía uno.
      ciudad: formData.idciudad || cliente.idciudad,
      // Se busca el nombre (label) del departamento y país correspondiente al ID seleccionado.
      // [EXPERT_ANALYSIS]: QUÉ: Se eliminó el campo 'departamento' del payload. - POR QUÉ: Para mantener la sincronización con el backend, donde se eliminó el manejo de este campo para evitar errores con la base de datos, ya que la columna 'sDepartamento' no existe.
      // [EXPERT_ANALYSIS]: QUÉ: Se eliminó el campo 'pais' del payload. - POR QUÉ: Para mantener la sincronización con el backend, donde se eliminó el manejo de este campo para evitar errores con la base de datos, ya que la columna 'sPais' no existe.
    };

    onSave(payload); // Se envía el payload formateado al componente padre.
    onClose();
  };

  const footerContent = (
    <div>
      {/* <Button label={t.cancel} icon="pi pi-times" onClick={onClose} className="p-button-text" /> */}
      {/* [EXPERT_COMMENT]: Línea original comentada por Andrés Baena - Se preserva la versión anterior para mantener un historial del cambio. */}
      <Button
        id="btn-editar-cliente-cancelar"
        label={t.cancel}
        icon="pi pi-times"
        onClick={onClose}
        className="p-button-secondary"
      />
      {/* [EXPERT_ANALYSIS]: QUÉ: Se ha modificado la clase del botón de 'Cancelar' de "p-button-text" a "p-button-secondary". - POR QUÉ: Se aplica el mismo criterio de unificación de estilos que en el modal de 'Crear Cliente'. La clase `p-button-secondary` proporciona una apariencia visualmente cohesiva con el botón primario ('Guardar'), estableciendo una jerarquía clara sin que los botones parezcan de aplicaciones diferentes. - IMPACTO: Garantiza la consistencia de la interfaz de usuario (UI) en todos los modales de gestión de clientes, mejorando la predictibilidad y la experiencia del usuario. */}
      <Button
        id="btn-editar-cliente-guardar"
        label={t.save}
        icon="pi pi-check"
        onClick={handleSubmit}
        autoFocus
      />
    </div>
  );

  return (
    <Dialog
      header={t.editClient}
      visible={isOpen}
      style={{ width: "60%", maxWidth: "350px" }}
      onHide={onClose}
      footer={footerContent}
    >
      <div className="p-fluid">
        <div className="p-field">
          <label htmlFor="sNombreCliente">{t.name}</label>
          <InputText
            id="editar-cliente-nombre-input"
            name="sNombreCliente"
            value={formData.sNombreCliente || ""}
            onChange={handleChange}
          />
        </div>
        <div className="p-field">
          <label htmlFor="sTelefono">{t.phone1}</label>
          <InputText
            id="editar-cliente-telefono-input"
            name="sTelefono"
            value={formData.sTelefono || ""}
            onChange={handleChange}
          />
        </div>
        {/* [EXPERT_COMMENT]: Comentado: Campo sTelefono2 - Se comenta todo el bloque del campo sTelefono2 para deshabilitarlo temporalmente. */}
        {/*
                <div className="p-field">
                    <label htmlFor="sTelefono2">{t.phone2}</label>
                    <InputText id="sTelefono2" name="sTelefono2" value={formData.sTelefono2 || ''} onChange={handleChange} />
                </div>
                */}
        <div className="p-field">
          <label htmlFor="sMarca">{t.brand}</label>
          <InputText
            id="editar-cliente-marca-input"
            name="sMarca"
            value={formData.sMarca || ""}
            onChange={handleChange}
          />
        </div>
        <div className="p-field">
          <label htmlFor="idpais">{t.country}</label>
          <Dropdown
            id="editar-cliente-pais-dropdown"
            name="idpais"
            value={formData.idpais}
            options={paises}
            onChange={handleChange}
            optionLabel="label" // [EXPERT_COMMENT]: Corregido: optionLabel para País - Se cambia a 'label' para coincidir con el formato de los objetos en el array 'paises'.
            optionValue="value" // [EXPERT_COMMENT]: Corregido: optionValue para País - Se cambia a 'value' para coincidir con el formato de los objetos en el array 'paises'.
            placeholder={t.selectCountry}
          />
        </div>
        <div className="p-field">
          <label htmlFor="iddepartamento">{t.department}</label>
          <Dropdown
            id="editar-cliente-departamento-dropdown"
            name="iddepartamento"
            value={formData.iddepartamento}
            options={departamentos}
            onChange={handleChange}
            optionLabel="label" // [EXPERT_COMMENT]: Corregido: optionLabel para Departamento - Se cambia a 'label' para coincidir con el formato de los objetos en el array 'departamentos'.
            optionValue="value" // [EXPERT_COMMENT]: Corregido: optionValue para Departamento - Se cambia a 'value' para coincidir con el formato de los objetos en el array 'departamentos'.
            placeholder={t.selectDepartment}
            disabled={!formData.idpais}
          />
        </div>
        <div className="p-field">
          <label htmlFor="idciudad">{t.city}</label>
          <Dropdown
            id="editar-cliente-ciudad-dropdown"
            name="idciudad"
            value={formData.idciudad}
            options={ciudades}
            onChange={handleChange}
            optionLabel="label" // [EXPERT_COMMENT]: Corregido: optionLabel para Ciudad - Se cambia a 'label' para coincidir con el formato de los objetos en el array 'ciudades'.
            optionValue="value" // [EXPERT_COMMENT]: Corregido: optionValue para Ciudad - Se cambia a 'value' para coincidir con el formato de los objetos en el array 'ciudades'.
            placeholder={t.selectCity}
            disabled={!formData.iddepartamento}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default EditarClientes;
